import { useCallback, useEffect, useState } from 'react';
import { GOOGLE_CLIENT_ID } from '../config/site';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const STORAGE_KEY = 'feldman.dev:google-credential';

export type SignedInUser = {
  name: string;
  email: string;
  picture?: string;
  /** Unix seconds at which the ID token stops being accepted. */
  expiresAt: number;
};

declare global {
  interface Window {
    google?: any;
  }
}

/**
 * Decode a JWT payload for display purposes only.
 *
 * This is deliberately signature-blind: nothing here is a security decision.
 * The Lambda verifies the token against Google's JWKS before trusting it, so
 * the worst a tampered local token can do is show a wrong name until the next
 * API call rejects it.
 */
const decode = (token: string): SignedInUser | null => {
  try {
    const [, payload] = token.split('.');
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    );
    if (!json.email || !json.exp) return null;
    return {
      name: json.name || String(json.email).split('@')[0],
      email: json.email,
      picture: json.picture,
      expiresAt: json.exp,
    };
  } catch {
    return null;
  }
};

const isFresh = (user: SignedInUser | null): user is SignedInUser =>
  !!user && user.expiresAt * 1000 > Date.now();

let scriptPromise: Promise<void> | null = null;

/** Load the Google Identity Services script once per page. */
const loadGis = (): Promise<void> => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Sign-In'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Google Sign-In state for the comments UI.
 *
 * Google ID tokens are short-lived (about an hour). Rather than refreshing
 * them, an expired token is simply dropped and the reader is asked to sign in
 * again the next time they want to post. Comments are a low-frequency action,
 * so the extra click is cheaper than maintaining a refresh flow.
 */
export const useGoogleAuth = () => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<SignedInUser | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const configured = GOOGLE_CLIENT_ID !== '';

  // Restore a previous session, discarding it if the token already expired.
  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    const decoded = decode(stored);
    if (isFresh(decoded)) {
      setToken(stored);
      setUser(decoded);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;

    loadGis()
      .then(() => {
        if (cancelled) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: ({ credential }: { credential: string }) => {
            const decoded = decode(credential);
            if (!decoded) {
              setError('That sign-in did not work. Please try again.');
              return;
            }
            window.localStorage.setItem(STORAGE_KEY, credential);
            setToken(credential);
            setUser(decoded);
            setError(null);
          },
        });
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Google Sign-In.');
      });

    return () => {
      cancelled = true;
    };
  }, [configured]);

  /** Paint the official Google button into a container element. */
  const renderButton = useCallback(
    (element: HTMLElement | null) => {
      if (!element || !ready || !window.google?.accounts?.id) return;
      element.innerHTML = '';
      window.google.accounts.id.renderButton(element, {
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
      });
    },
    [ready],
  );

  const signOut = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
    window.google?.accounts?.id?.disableAutoSelect?.();
  }, []);

  /** Drop the session after the API rejects the token as expired. */
  const invalidate = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return { configured, ready, token, user, error, renderButton, signOut, invalidate };
};
